import React from 'react'

import LoadingProductLineSelection from 'Presentation/Components/Loading/LoadingProductLineSelection'
import ProductionLineSelection from 'Presentation/Components/ProductLineSelection'
import KumopackProductBox from 'Presentation/Components/KumopackProductBox'
import KumopackColorPreview from 'Presentation/Components/KumopackColorPreview'
import { SkeletonThumbnailWithTitle } from 'Presentation/Components/Loading/Skeleton'

import useViewModel, { Props } from './ViewModel'
import clsx from 'clsx'
import { isMobileDevice } from '_metronic/assets/ts/_utils'

const DocumentQuotationFormProductAddFormView: React.FC<Props> = props => {
	const {
		currentStep,
		isLoadingProductLine,
		isLoadingProductCategory,
		isLoadingProperties,
		productLineData,
		productCategoryData,
		selectProductLine,
		selectProductCategory,
		currentActiveProductCategory,
		currentActiveProductLine,
		productCategoryRef,
		mappedOptions,
		mappedProducts,
		mappedAccessories,
		mappedThicknesses,
		mappedOuterMaterials,
		mappedInnerMaterials,
		mappedPrintingOptions,
		mappedFormings,
		propertyState,
		errors,
		showPrevButton,
		changeProductLineActive,
		changeProductCategoryActive,
		onSelectProperty,
		onPrevStep,
		onNextStep,
	} = useViewModel(props)

	return (
		<React.Fragment>
			{/* Product Line Selection */}
			<div id="product-form-quotation-anchor"></div>

			{currentStep === 0 && (
				<div className="col-12">
					<div
						className={clsx({
							'd-none': !isLoadingProductLine,
							'd-block': isLoadingProductLine,
						})}>
						<LoadingProductLineSelection title="Step 1 - เลือก Product Line" />
					</div>

					<div
						className={clsx({
							'd-none': isLoadingProductLine,
							'd-block': !isLoadingProductLine,
						})}>
						<ProductionLineSelection
							title="Step 1 - เลือก Product Line"
							products={productLineData}
							onClickCard={id =>
								selectProductLine(id, productLineData.find(x => x.id === id)?.hasChild ?? false)
							}
							customActive={currentActiveProductLine}
							setCustomActive={changeProductLineActive}
						/>
					</div>

					<div
						className={clsx({
							'd-none': !isLoadingProductCategory,
							'd-block': isLoadingProductCategory,
						})}>
						<LoadingProductLineSelection title="Step 1.1 - เลือก Product Category" />
					</div>

					<div
						id="product-category"
						ref={productCategoryRef}
						className={clsx({
							'd-none': isLoadingProductCategory || productCategoryData.length === 0,
							'd-block': !isLoadingProductCategory,
						})}>
						<ProductionLineSelection
							title="Step 1.1 - เลือก Product Category"
							products={productCategoryData}
							onClickCard={id => selectProductCategory(id)}
							customActive={currentActiveProductCategory}
							setCustomActive={changeProductCategoryActive}
						/>
					</div>
				</div>
			)}

			{/* Property Selection */}
			{currentStep === 1 && (
				<div className="col-12">
					<div className="card border border-kumopack-grey-200">
						<div className="card-body">
							<div className="row gy-5">
								<div className="col-12">
									<h3 className="text-kumopack-grey-900">
										Step 2 - เลือกส่วนเสริมของสินค้า (Options)
									</h3>
								</div>

								{/* Product  */}
								<div className="col-12 text-kumopack-grey-400">
									2.1 เลือกสินค้าที่ผลิตได้ (Products) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(6)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`product-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedProducts.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-products-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.product === item.id}
												error={errors.product}
												onClick={() => onSelectProperty('product', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedProducts.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Options */}
								<div className="col-12 text-kumopack-grey-400">
									2.2 เลือกประเภทส่วนเสริม (Options) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`options-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedOptions.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-options-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.options.includes(item.id)}
												error={errors.options}
												onClick={() => onSelectProperty('options', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedOptions.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Accessories */}
								<div className="col-12 text-kumopack-grey-400">
									2.3 เลือกประเภทส่วนเสริม (Accessories) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`accessories-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedAccessories.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-accessories-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.accessories.includes(item.id)}
												error={errors.accessories}
												onClick={() => onSelectProperty('accessories', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedAccessories.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Thicknesses */}
								<div className="col-12 text-kumopack-grey-400">
									2.4 ความหนาของสินค้า (Thicknesses) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`thicknesses-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedThicknesses.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-thicknesses-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.thickness === item.id}
												error={errors.thickness}
												onClick={() => onSelectProperty('thickness', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedThicknesses.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Outer Materials */}
								<div className="col-12 text-kumopack-grey-400">
									2.5 เลือกประเภทสีด้านนอก (Outer Materials) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`outer-materials-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedOuterMaterials.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-outer-materials-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3"
											style={{
												width: !isMobileDevice() ? 'max-content' : '100%',
											}}>
											<KumopackColorPreview
												{...item}
												active={propertyState.outerMaterial === item.id}
												error={errors.outerMaterial}
												onClick={() => onSelectProperty('outerMaterial', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedOuterMaterials.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Inner Materials */}
								<div className="col-12 text-kumopack-grey-400">
									2.6 เลือกประเภทสีด้านใน (Inner Materials) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											key={`inner-materials-skeleton-${idx}`}
											className="col-12 col-sm-6 col-lg-3"
											style={{
												width: !isMobileDevice() ? 'max-content' : '100%',
											}}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedInnerMaterials.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-inner-materials-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3"
											style={{
												width: !isMobileDevice() ? 'max-content' : '100%',
											}}>
											<KumopackColorPreview
												{...item}
												active={propertyState.innerMaterial === item.id}
												error={errors.innerMaterial}
												onClick={() => onSelectProperty('innerMaterial', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedInnerMaterials.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Printing Options */}
								<div className="col-12 text-kumopack-grey-400">
									2.7 เลือกประเภทงานพิมพ์ (Printing Options) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`printing-options-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedPrintingOptions.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-printing-options-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.printingOption === item.id}
												error={errors.printingOption}
												onClick={() => onSelectProperty('printingOption', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedPrintingOptions.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}

								{/* Forming */}
								<div className="col-12 text-kumopack-grey-400">
									2.8 เลือกประเภทการขึ้นรูป (Forming) <span className="required" />
								</div>

								{isLoadingProperties &&
									[...Array(4)].map((_, idx) => (
										<div
											className="col-12 col-sm-6 col-lg-3 col-xl-3"
											key={`forming-skeleton-${idx}`}>
											<SkeletonThumbnailWithTitle />
										</div>
									))}
								{!isLoadingProperties &&
									mappedFormings.map((item: any, itemIdx: any) => (
										<div
											key={`${item.nameTh}-formings-${itemIdx}`}
											className="col-12 col-sm-6 col-lg-3 col-xl-3">
											<KumopackProductBox
												{...item}
												active={propertyState.forming === item.id}
												error={errors.forming}
												onClick={() => onSelectProperty('forming', item.id)}
											/>
										</div>
									))}
								{!isLoadingProperties && mappedFormings.length === 0 && (
									<div className="text-kumopack-grey-500 text-center">ไม่พบข้อมูล</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{currentStep === 2 && (
				<div className="col-12">
					<div className="card border border-kumopack-grey-200">
						<div className="card-body">
							<div className="row gy-5">
								<div className="col-12">
									<h3 className="text-kumopack-grey-900">
										Step 3 - เลือกขนาดของสินค้า (Product Size)
									</h3>
								</div>

								{/* begin:: Product Dimension */}
								<div className="col-12 fs-6 mb-4 text-kumopack-grey-400">
									4.1 Product Dimensions
									<span className="required" />
								</div>
								<div className="col-12 mb-4">
									<span className="fs-6">
										เลือกหน่วย:{' '}
										{errors.unit && (
											<div className="fv-plugins-message-container mt-2 d-inline-block">
												<div className="fv-help-block">({errors.unit})</div>
											</div>
										)}
									</span>

									<div
										className="mt-4 d-flex flex-column flex-xl-row align-items-xl-center"
										style={{ gap: '12px' }}>
										<div className="form-check">
											<input
												id="dimension_mm"
												type="radio"
												className={clsx('form-check-input', {
													'is-invalid': errors.unit,
												})}
												value="mm"
												checked={propertyState.unit === 'mm'}
												onChange={e => onSelectProperty('unit', e.target.value)}
											/>

											<label
												htmlFor="dimension_mm"
												className={clsx('text-kumopack-grey-700', {
													'text-kumopack-grey-700': !errors.unit,
													'text-kumopack-error-500': errors.unit,
												})}>
												มิลลิเมตร (mm)
											</label>
										</div>

										<div className="form-check">
											<input
												id="dimension_cm"
												type="radio"
												className="form-check-input"
												value="cm"
												checked={propertyState.unit === 'cm'}
												onChange={e => onSelectProperty('unit', e.target.value)}
											/>

											<label
												htmlFor="dimension_cm"
												className={clsx('text-kumopack-grey-700', {
													'text-kumopack-grey-700': !errors.unit,
													'text-kumopack-error-500': errors.unit,
												})}>
												เซนติเมตร (cm)
											</label>
										</div>

										<div className="form-check">
											<input
												id="dimension_ln"
												type="radio"
												className="form-check-input"
												value="ln"
												checked={propertyState.unit === 'ln'}
												onChange={e => onSelectProperty('unit', e.target.value)}
											/>

											<label
												htmlFor="dimension_ln"
												className={clsx('text-kumopack-grey-700', {
													'text-kumopack-grey-700': !errors.unit,
													'text-kumopack-error-500': errors.unit,
												})}>
												นิ้ว (In)
											</label>
										</div>
									</div>
								</div>
							</div>
							{/* end:: Product Dimension */}

							{/* begin:: Measurement Approach */}
							<div className="col-12 mb-4">
								<span className="fs-6">
									ลักษณะการวัด:{' '}
									{errors.measurementApproach && (
										<div className="fv-plugins-message-container mt-2 d-inline-block">
											<div className="fv-help-block">({errors.measurementApproach})</div>
										</div>
									)}
								</span>

								<div
									className="mt-4 d-flex flex-column flex-xl-row align-items-xl-center"
									style={{ gap: '12px' }}>
									<div className="form-check">
										<input
											id="product_size"
											type="radio"
											className={clsx('form-check-input', {
												'is-invalid': errors.measurementApproach,
											})}
											value="product_size"
											// @ts-ignore
											checked={propertyState.measurementApproach === 'product_size'}
											onChange={e => onSelectProperty('measurementApproach', e.target.value)}
										/>

										<label
											htmlFor="product_size"
											className={clsx('text-kumopack-grey-700', {
												'text-kumopack-grey-700': !errors.measurementApproach,
												'text-kumopack-error-500': errors.measurementApproach,
											})}>
											ขนาดสินค้า
										</label>
									</div>

									<div className="form-check">
										<input
											id="inside"
											type="radio"
											className={clsx('form-check-input', {
												'is-invalid': errors.measurementApproach,
											})}
											value="inside"
											// @ts-ignore
											checked={propertyState.measurementApproach === 'inside'}
											onChange={e => onSelectProperty('measurementApproach', e.target.value)}
										/>

										<label
											htmlFor="inside"
											className={clsx('text-kumopack-grey-700', {
												'text-kumopack-grey-700': !errors.measurementApproach,
												'text-kumopack-error-500': errors.measurementApproach,
											})}>
											ขนาดภายใน
										</label>
									</div>

									<div className="form-check">
										<input
											id="outside"
											type="radio"
											className={clsx('form-check-input', {
												'is-invalid': errors.measurementApproach,
											})}
											value="outside"
											// @ts-ignore
											checked={propertyState.measurementApproach === 'outside'}
											onChange={e => onSelectProperty('measurementApproach', e.target.value)}
										/>

										<label
											htmlFor="outside"
											className={clsx('text-kumopack-grey-700', {
												'text-kumopack-grey-700': !errors.measurementApproach,
												'text-kumopack-error-500': errors.measurementApproach,
											})}>
											ขนาดภายนอก
										</label>
									</div>

									<div className="form-check">
										<input
											id="score_line"
											type="radio"
											className={clsx('form-check-input', {
												'is-invalid': errors.measurementApproach,
											})}
											value="score_line"
											// @ts-ignore
											checked={propertyState.measurementApproach === 'score_line'}
											onChange={e => onSelectProperty('measurementApproach', e.target.value)}
										/>

										<label
											htmlFor="score_line"
											className={clsx('text-kumopack-grey-700', {
												'text-kumopack-grey-700': !errors.measurementApproach,
												'text-kumopack-error-500': errors.measurementApproach,
											})}>
											ขนาดตามทับเส้น Score Line
										</label>
									</div>
								</div>
							</div>
							{/* end:: Measurement Approach */}

							{/* begin:: Size */}
							<div className="col-12">
								<div className="row gy-3">
									<div className="col-12 col-xl-4">
										<label className="text-kumopack-grey-700 mb-1">ความกว้างกล่อง</label>
										<div className="position-relative">
											<input
												style={{ backgroundImage: 'none' }}
												className={clsx('form-control form-control-sm', {
													'is-invalid': errors.width,
												})}
												placeholder="ความกว้างกล่อง"
												value={propertyState.width ?? ''}
												onChange={e => onSelectProperty('width', e.target.value)}
											/>
											<div
												className="position-absolute text-kumopack-grey-400 fs-7"
												style={{
													right: '4%',
													top: '50%',
													transform: 'translateY(-50%)',
												}}>
												{propertyState.unit}
											</div>
										</div>
									</div>

									<div className="col-12 col-xl-4">
										<label className="text-kumopack-grey-700 mb-1">ความยาวกล่อง</label>
										<div className="position-relative">
											<input
												style={{ backgroundImage: 'none' }}
												className={clsx('form-control form-control-sm', {
													'is-invalid': errors.length,
												})}
												placeholder="ความยาวกล่อง"
												value={propertyState.length ?? ''}
												onChange={e => onSelectProperty('length', e.target.value)}
											/>
											<div
												className="position-absolute text-kumopack-grey-400 fs-7"
												style={{ right: '4%', top: '50%', transform: 'translateY(-50%)' }}>
												{propertyState.unit}
											</div>
										</div>
									</div>

									<div className="col-12 col-xl-4 mb-4">
										<label className="text-kumopack-grey-700 mb-1">ความสูงกล่อง</label>
										<div className="position-relative">
											<input
												style={{ backgroundImage: 'none' }}
												className={clsx('form-control form-control-sm', {
													'is-invalid': errors.height,
												})}
												placeholder="ความสูงกล่อง"
												value={propertyState.height ?? ''}
												onChange={e => onSelectProperty('height', e.target.value)}
											/>
											<div
												className="position-absolute text-kumopack-grey-400 fs-7"
												style={{ right: '4%', top: '50%', transform: 'translateY(-50%)' }}>
												{propertyState.unit}
											</div>
										</div>
									</div>

									<div className="col-12 fs-6 my-4 text-kumopack-grey-400">
										4.5 ข้อมูลอื่นๆ <span className="required" />
									</div>
									<div className="row">
										<div className="col-12">
											<div className="row gy-3">
												<div className="col-12 col-xl-6">
													<label className="text-kumopack-grey-700 mb-1">
														น้ำหนักสินค้า (Weight)
													</label>
													<div className="position-relative">
														<input
															style={{ backgroundImage: 'none' }}
															className={clsx('form-control form-control-sm', {
																'is-invalid': errors.weightGram,
															})}
															placeholder="น้ำหนักสินค้า"
															value={propertyState.weightGram ?? ''}
															onChange={e => onSelectProperty('weightGram', e.target.value)}
														/>
														<div
															className="position-absolute text-kumopack-grey-400 fs-7"
															style={{ right: '3%', top: '50%', transform: 'translateY(-50%)' }}>
															กรัม
														</div>
													</div>
												</div>

												<div className="col-12 col-xl-6">
													<label className="text-kumopack-grey-700 mb-1">
														Vertical stack จำนวนการเรียงซ้อน
													</label>
													<div className="position-relative">
														<input
															style={{ backgroundImage: 'none' }}
															className={clsx('form-control form-control-sm', {
																'is-invalid': errors.verticalStack,
															})}
															placeholder="Verticle Stack (Lock 1-20 ชั้น)"
															value={propertyState.verticalStack ?? ''}
															onChange={e => onSelectProperty('verticalStack', e.target.value)}
														/>
														<div
															className="position-absolute text-kumopack-grey-400 fs-7"
															style={{ right: '3%', top: '50%', transform: 'translateY(-50%)' }}>
															ชั้น
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							{/* end:: Size */}
						</div>
					</div>
				</div>
			)}

			<div className="col-12">
				<div
					className="d-flex flex-column flex-lg-row justify-content-center justify-content-lg-end align-items-center"
					style={{ gap: '12px' }}>
					{showPrevButton && (
						<button
							className="btn white-button text-kumopack-grey-900 fw-bold"
							onClick={onPrevStep}>
							ย้อนกลับ
						</button>
					)}
					<button
						className="btn btn-kumopack-primary-700 text-kumopack-base-white fw-bold"
						onClick={onNextStep}>
						{currentStep === 3 ? 'เพิ่มสินค้า' : 'ต่อไป'}
					</button>
				</div>
			</div>
		</React.Fragment>
	)
}

export default DocumentQuotationFormProductAddFormView
